import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Flag, RotateCcw, Send, GraduationCap, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import {
  saveBackup, loadBackup, mergeWithBackup, clearBackup,
  enqueueFailedSave, flushSyncQueue
} from '../../utils/examOffline';

const SUBJECTS = ['Mathematics', 'Physics', 'Chemistry'];

// KaTeX block renderer (for the dedicated formula field — always pure LaTeX)
const renderFormula = (formula) => {
  if (!formula) return '';
  try {
    return katex.renderToString(formula, { throwOnError: false, displayMode: true });
  } catch {
    return formula;
  }
};

// Escape HTML special chars so plain text is safe to inject via dangerouslySetInnerHTML
const escapeHtml = (str) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Smart inline renderer:
 *  - Splits text on $...$ inline-math delimiters and \(...\) / \[...\].
 *  - Runs KaTeX only on the math segments.
 *  - Returns plain-text segments as HTML-escaped strings so KaTeX never
 *    touches them — this prevents merged words, italic formatting, and
 *    missing spaces that KaTeX's math-mode parser introduces on plain text.
 */
const renderInline = (text) => {
  if (!text) return '';

  // Patterns that indicate the whole string is a LaTeX expression
  // (used by the formula field path — renderFormula handles those).
  // For question text / option text we do segment-based rendering.

  // Split on: $...$  or  \(...\)  or  \[...\]
  const parts = [];
  // Regex: captures delimiters + content
  // Groups: [1] = $...$ content, [2] = \(...\) content, [3] = \[...\] content
  const pattern = /\$([^$]+)\$|\\\(([\s\S]*?)\\\)|\\\[([\s\S]*?)\\\]/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    // Plain text before this math segment
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    const mathContent = match[1] ?? match[2] ?? match[3];
    const isDisplay   = match[3] !== undefined; // \[...\] is display mode
    parts.push({ type: 'math', value: mathContent, display: isDisplay });
    lastIndex = pattern.lastIndex;
  }

  // Remaining plain text after last match
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  // If no math delimiters were found, return the whole string as plain text
  if (parts.length === 0) {
    return escapeHtml(text);
  }

  // Build the HTML output
  return parts.map(part => {
    if (part.type === 'text') {
      return escapeHtml(part.value);
    }
    try {
      return katex.renderToString(part.value, {
        throwOnError: false,
        displayMode: part.display,
      });
    } catch {
      return escapeHtml(part.value);
    }
  }).join('');
};

/**
 * stripLeadingNumber
 * ------------------
 * Removes any manually typed question number prefix from stored question text
 * so that the auto-generated position number badge is never duplicated.
 *
 * Patterns stripped (case-insensitive, any leading whitespace):
 *   "1. text"   "12. text"   "Q3. text"   "1) text"   "(1) text"
 *   "Q.1 text"  "Q1 text"
 *
 * Examples:
 *   "1. What is the sun" → "What is the sun"
 *   "Q12. What is the earth" → "What is the earth"
 *   "What is the moon"  → "What is the moon"  (unchanged — no prefix found)
 */
const stripLeadingNumber = (text) => {
  if (!text) return text;
  return text
    // "1. " or "12. " or "1) " or "(1) "
    .replace(/^\s*(?:Q\.?)?\(?\d{1,3}[.):]\)?\s*/i, '')
    // "Q3 " (Q followed by digits and a space, no punctuation)
    .replace(/^\s*Q\d{1,3}\s+/i, '')
    .trimStart();
};

// Question status helper
const getQuestionStatus = (answer) => {
  if (!answer) return 'not-visited';
  const { isVisited, selectedAnswer, isMarkedForReview } = answer;
  if (!isVisited) return 'not-visited';
  if (isMarkedForReview && selectedAnswer) return 'marked-answered';
  if (isMarkedForReview) return 'marked';
  if (selectedAnswer) return 'answered';
  return 'not-answered';
};

const STATUS_CLASSES = {
  'not-visited': 'bg-gray-200 text-gray-600',
  'not-answered': 'bg-red-500 text-white',
  'answered': 'bg-green-500 text-white',
  'marked': 'bg-amber-400 text-white',
  'marked-answered': 'bg-violet-500 text-white',
};

export default function ExamPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [test, setTest] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSubject, setCurrentSubject] = useState('Mathematics');
  const [timeLeft, setTimeLeft] = useState(180 * 60);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBackWarning, setShowBackWarning] = useState(false);
  const [violations, setViolations] = useState(0);

  const saveTimeoutRef  = useRef(null);
  const timerRef        = useRef(null);
  const autoSubmitTimeoutRef = useRef(null); // hard-fallback for browser throttling
  const endTimeRef      = useRef(null);    // actual exam end timestamp
  const clockOffsetRef  = useRef(0);       // serverTime - clientTime (syncs clocks)
  const violationSentRef = useRef(0);      // debounce violation reports
  const attemptIdRef    = useRef(null);
  const timeLeftRef     = useRef(timeLeft);
  const submittedRef    = useRef(false); // closure-safe submission lock
  const answersRef      = useRef([]);    // always-current answers for flush

  useEffect(() => { timeLeftRef.current = timeLeft; }, [timeLeft]);

  /* ─── Network status listeners ─── */
  useEffect(() => {
    const goOnline = async () => {
      setIsOnline(true);
      toast.success('🌐 Connection restored', { id: 'net-online', duration: 2500 });
      // Flush queued saves
      if (attemptIdRef.current) {
        const apiPut = (data) => api.put(`/exam/save-answer/${attemptIdRef.current}`, data);
        const flushed = await flushSyncQueue(attemptIdRef.current, apiPut);
        if (flushed > 0) toast.success(`✅ ${flushed} saved answer(s) synced`, { id: 'sync-ok', duration: 2000 });
      }
    };
    const goOffline = () => {
      setIsOnline(false);
      toast.error('📡 You are offline. Answers are saved locally.', { id: 'net-offline', duration: 0 });
    };
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  /* ─── Tab-switch violation detector ─── */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden' && !submittedRef.current) {
        const now = Date.now();
        // Debounce: ignore if we just logged one (prevents double-fire)
        if (now - violationSentRef.current < 3000) return;
        violationSentRef.current = now;

        setViolations(prev => {
          const next = prev + 1;
          // Report to server
          if (attemptIdRef.current) {
            api.post(`/exam/violation/${attemptIdRef.current}`).catch(() => {});
          }
          if (next >= 3) {
            toast.dismiss();
            toast.error('🚫 3 violations detected. Auto-submitting exam...', { id: 'violation-auto-submit', duration: 5000 });
            setTimeout(() => submitExam(true), 1500);
          } else {
            toast.error(`⚠️ Tab switch detected! Violation ${next}/3. Exam will auto-submit on 3rd violation.`, { id: 'violation', duration: 4000 });
          }
          return next;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  /* ─── Back-navigation guard ─── */
  useEffect(() => {
    if (loading) return; // wait until exam is loaded

    // Push a duplicate history entry so the back button has something to pop
    window.history.pushState({ examGuard: true }, '', window.location.href);

    const handlePopState = (e) => {
      if (submittedRef.current) return; // exam done — allow normal nav
      // Re-push so the user stays on the page
      window.history.pushState({ examGuard: true }, '', window.location.href);
      setShowBackWarning(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /* ─── Tab close / refresh guard ─── */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (submittedRef.current) return; // already submitted — allow
      e.preventDefault();
      e.returnValue = 'Exam is in progress. Your answers are saved. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Initialize exam
  useEffect(() => {
    startExam();
    return () => {
      clearInterval(timerRef.current);
      clearTimeout(saveTimeoutRef.current);
      clearTimeout(autoSubmitTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const startExam = async () => {
    try {
      const { data } = await api.post(`/exam/start/${testId}`);
      const { attempt: att, test: t, serverTime } = data.data;

      setTest(t);
      setAttempt(att);
      attemptIdRef.current = att._id;

      // ── Sync client clock to server time ──
      clockOffsetRef.current = serverTime - Date.now();

      // Initialize answers from server
      const serverAnswers = att.answers.map(a => ({
        questionId:        a.questionId,
        questionNumber:    a.questionNumber,
        selectedAnswer:    a.selectedAnswer    || null,
        isMarkedForReview: a.isMarkedForReview || false,
        isVisited:         a.isVisited         || false,
        timeSpent:         a.timeSpent         || 0
      }));

      // ── Merge with localStorage backup (local wins for non-null values) ──
      const backup = loadBackup(att._id);
      const merged = mergeWithBackup(serverAnswers, backup?.answers || null);
      setAnswers(merged);
      answersRef.current = merged;

      // Restore position (prefer backup position if more recent)
      const backupMeta = backup?.meta;
      if (backupMeta?.currentIndex !== undefined) {
        setCurrentIndex(backupMeta.currentIndex);
        if (backupMeta.currentSubject) setCurrentSubject(backupMeta.currentSubject);
      } else {
        if (att.currentQuestion !== undefined) setCurrentIndex(att.currentQuestion);
        if (att.currentSubject) setCurrentSubject(att.currentSubject);
      }

      // Set timer using server-synced time
      const adjustedNow = Date.now() + clockOffsetRef.current;
      const elapsed = Math.floor((adjustedNow - new Date(att.startTime).getTime()) / 1000);
      const remaining = Math.max(0, t.duration * 60 - elapsed);
      setTimeLeft(remaining);

      startTimer(remaining);
    } catch (err) {
      const status  = err.response?.status;
      const msg     = err.response?.data?.message || 'Failed to start exam';
      const scheduled = err.response?.data?.scheduledFor;

      if (status === 400 && msg.toLowerCase().includes('already submitted')) {
        // Already submitted — go straight to result
        toast.success('You have already submitted this exam. Redirecting to results...');
        navigate(`/student/result/${testId}`);
        return;
      }

      if (status === 403 && scheduled) {
        // Exam not yet started per test schedule
        const examTime = new Date(scheduled).toLocaleString('en-IN', {
          dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata'
        });
        toast.error(`Exam starts at ${examTime}. Please come back then.`, { duration: 6000 });
      } else {
        toast.error(msg);
      }
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const startTimer = (initial) => {
    let remaining = initial;
    // Set the actual exam end time using server-synced clock
    const adjustedNow = Date.now() + clockOffsetRef.current;
    endTimeRef.current = adjustedNow + remaining * 1000;

    // setInterval for display updates (throttled in background tabs)
    timerRef.current = setInterval(() => {
      const adjustedNow = Date.now() + clockOffsetRef.current;
      const actualRemaining = Math.max(0, Math.ceil((endTimeRef.current - adjustedNow) / 1000));
      setTimeLeft(actualRemaining);
      timeLeftRef.current = actualRemaining;
      if (actualRemaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        if (!submittedRef.current) {
          handleAutoSubmit();
        }
      }
    }, 1000);

    // setTimeout as a hard fallback — not throttled by browsers even in background
    if (remaining > 0) {
      autoSubmitTimeoutRef.current = setTimeout(() => {
        clearInterval(timerRef.current);
        timerRef.current = null;
        if (!submittedRef.current) {
          handleAutoSubmit();
        }
      }, remaining * 1000 + 500); // 500ms buffer to avoid race with setInterval
    }
  };

  const handleAutoSubmit = async () => {
    if (submittedRef.current) return; // already submitted — do nothing
    submittedRef.current = true;
    toast.dismiss();                  // clear any pending toasts
    toast.error('⏰ Time is up! Auto-submitting...', { id: 'auto-submit', duration: 3000 });
    await submitExam(true);
  };

  // Debounced save — writes to localStorage first, then API (with offline queue)
  const saveAnswer = useCallback(async (answerData) => {
    if (!attemptIdRef.current) return;

    // 1. Always update localStorage backup immediately
    saveBackup(attemptIdRef.current, answersRef.current, currentIndex, currentSubject);

    // 2. Debounced API call
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      if (submittedRef.current) return;
      try {
        await api.put(`/exam/save-answer/${attemptIdRef.current}`, answerData);
      } catch (err) {
        // Network failure — enqueue for later sync
        enqueueFailedSave(attemptIdRef.current, answerData);
        console.warn('Save queued (offline):', answerData.questionId);
      }
    }, 500);
  }, [currentIndex, currentSubject]);

  // Mark current as visited when index changes
  useEffect(() => {
    if (answers.length === 0 || loading) return;
    const ans = answers[currentIndex];
    if (ans && !ans.isVisited) {
      const updated = [...answers];
      updated[currentIndex] = { ...updated[currentIndex], isVisited: true };
      setAnswers(updated);
      saveAnswer({ questionId: ans.questionId, isVisited: true, currentQuestion: currentIndex, currentSubject });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, loading]);

  const handleAnswerSelect = (option) => {
    const updated = [...answers];
    const ans = updated[currentIndex];
    const newSelected = ans.selectedAnswer === option ? null : option;
    updated[currentIndex] = { ...ans, selectedAnswer: newSelected, isVisited: true };
    setAnswers(updated);
    answersRef.current = updated; // keep ref in sync
    saveAnswer({ questionId: ans.questionId, selectedAnswer: newSelected, isVisited: true, currentQuestion: currentIndex, currentSubject });
  };

  const handleMarkForReview = () => {
    const updated = [...answers];
    const ans = updated[currentIndex];
    updated[currentIndex] = { ...ans, isMarkedForReview: !ans.isMarkedForReview, isVisited: true };
    setAnswers(updated);
    saveAnswer({ questionId: ans.questionId, isMarkedForReview: !ans.isMarkedForReview, isVisited: true });
    if (!ans.isMarkedForReview) goToNext();
  };

  const handleClearResponse = () => {
    const updated = [...answers];
    const ans = updated[currentIndex];
    updated[currentIndex] = { ...ans, selectedAnswer: null };
    setAnswers(updated);
    saveAnswer({ questionId: ans.questionId, selectedAnswer: null });
  };

  const goToNext = () => {
    if (currentIndex < answers.length - 1) setCurrentIndex(prev => prev + 1);
  };

  const goToPrev = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  const goToQuestion = (index) => {
    setCurrentIndex(index);
    const q = test?.questions?.[index]?.question;
    if (q?.subject) setCurrentSubject(q.subject);
    setPaletteOpen(false);
  };

  const jumpToSubject = (subject) => {
    const firstIndex = test?.questions?.findIndex(q => q.question?.subject === subject);
    if (firstIndex !== -1) {
      setCurrentIndex(firstIndex);
      setCurrentSubject(subject);
    }
  };

  const submitExam = async (isAuto = false) => {
    if (submittedRef.current && !isAuto) return;
    if (submitting) return;

    // Lock immediately
    submittedRef.current = true;
    setSubmitting(true);

    // Stop all timers
    clearInterval(timerRef.current);
    timerRef.current = null;
    clearTimeout(saveTimeoutRef.current);
    clearTimeout(autoSubmitTimeoutRef.current);
    autoSubmitTimeoutRef.current = null;

    const attemptId = attemptIdRef.current;

    try {
      // Submit — answers already saved via debounced saveAnswer on every interaction
      await api.post(`/exam/submit/${attemptId}`, {
        timeRemaining: timeLeftRef.current,
        isAutoSubmit: isAuto
      });

      // Clear localStorage backup
      clearBackup(attemptId);

      toast.dismiss();
      toast.success('Exam submitted successfully! 🎉', { id: 'submit-ok', duration: 3000 });
      navigate(`/student/result/${testId}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Submission failed';
      if (err.response?.status === 400 && msg.includes('already submitted')) {
        clearBackup(attemptId);
        toast.dismiss();
        toast.success('Exam submitted successfully! 🎉', { id: 'submit-ok', duration: 3000 });
        navigate(`/student/result/${testId}`);
        return;
      }
      submittedRef.current = false;
      setSubmitting(false);
      toast.error(msg, { id: 'submit-err' });
    }
  };

  // Format time
  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Stats
  const totalAnswered = answers.filter(a => a.selectedAnswer).length;
  const totalMarked = answers.filter(a => a.isMarkedForReview).length;
  const totalNotVisited = answers.filter(a => !a.isVisited).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!test) return null;

  const currentQuestion = test.questions?.[currentIndex]?.question;
  const currentAnswer = answers[currentIndex];
  const isTimeCritical = timeLeft < 300;

  // Group questions by subject for the palette
  const subjectGroups = SUBJECTS.reduce((acc, s) => {
    acc[s] = test.questions
      .map((q, i) => ({ ...q, index: i }))
      .filter(q => q.question?.subject === s);
    return acc;
  }, {});

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* TOP BAR */}
      <header className="bg-primary-800 text-white flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-2 border-b border-primary-700">
          <div className="flex items-center gap-3">
            {/* BITS Vizag Logo */}
            <img src="/logo.png" alt="BITS" className="h-9 w-auto bg-white rounded-md p-0.5 shadow-sm" onError={e => e.target.style.display = 'none'} />
            <div className="flex flex-col">
              <span className="text-xs font-bold leading-tight tracking-wide text-primary-100">BITS VIZAG</span>
              <span className="text-xs font-mono font-semibold tracking-wider text-white">{user?.hallTicketNumber || 'HT: ---'}</span>
            </div>
            {/* Network status badge */}
            {!isOnline ? (
              <span className="flex items-center gap-1 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold animate-pulse">
                <WifiOff size={11} /> Offline
              </span>
            ) : (
              <span className="flex items-center gap-1 bg-green-600/60 text-green-100 text-xs px-2 py-0.5 rounded-full font-medium">
                <Wifi size={11} /> Online
              </span>
            )}
            {/* Violations badge */}
            {violations > 0 && (
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${violations >= 3 ? 'bg-red-600 text-white animate-pulse' : 'bg-amber-500/80 text-white'}`}>
                ⚠️ {violations}/3
              </span>
            )}
          </div>
          <div className="text-center">
            <p className="text-primary-200 text-xs">Time Remaining</p>
            <p className={`font-bold text-xl font-mono ${isTimeCritical ? 'timer-critical text-red-300' : 'text-white'}`}>
              {formatTime(timeLeft)}
            </p>
          </div>
          <button onClick={() => setShowSubmitModal(true)}
            disabled={submitting}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors">
            <Send size={15} /> Submit
          </button>
        </div>

        {/* Subject Tabs */}
        <div className="flex border-b border-primary-700">
          {SUBJECTS.map(s => {
            const answered = answers.filter((a, i) => test.questions[i]?.question?.subject === s && a.selectedAnswer).length;
            const total = subjectGroups[s]?.length || 0;
            return (
              <button key={s} onClick={() => jumpToSubject(s)}
                className={`flex-1 py-2 text-xs sm:text-sm font-semibold transition-colors text-center ${
                  currentQuestion?.subject === s
                    ? 'bg-white text-primary-800 border-b-2 border-primary-600'
                    : 'text-primary-200 hover:bg-primary-700'
                }`}>
                <span className="block">{s}</span>
                <span className={`text-xs ${currentQuestion?.subject === s ? 'text-primary-600' : 'text-primary-300'}`}>
                  {answered}/{total}
                </span>
              </button>
            );
          })}
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div className="flex flex-1 overflow-hidden">
        {/* Question Area */}
        <main className="flex-1 flex flex-col overflow-hidden bg-white">
          {/* Question header */}
          <div className="bg-gray-50 border-b px-4 py-2 flex items-center gap-3 flex-shrink-0">
            <span className="bg-primary-600 text-white text-xs font-bold px-2.5 py-1 rounded-lg">
              Q.{(currentAnswer?.questionNumber) || currentIndex + 1}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              currentQuestion?.subject === 'Mathematics' ? 'bg-blue-100 text-blue-700' :
              currentQuestion?.subject === 'Physics' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>{currentQuestion?.subject}</span>
          </div>

          {/* Question text */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 scrollbar-thin">
            {currentQuestion ? (
              <>
                {/* Question Text (text & hybrid types) */}
                {currentQuestion.questionText && (
                  <p
                    className="exam-question-text text-gray-800 text-base mb-4 font-medium"
                    dangerouslySetInnerHTML={{
                      // stripLeadingNumber removes any manually typed prefix (e.g. "1. ", "Q3. ")
                      // so the auto-generated Q-badge above never produces duplicate numbering.
                      __html: renderInline(stripLeadingNumber(currentQuestion.questionText))
                    }}
                  />
                )}

                {/* LaTeX Formula */}
                {currentQuestion.formula && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg overflow-x-auto">
                    <div dangerouslySetInnerHTML={{ __html: renderFormula(currentQuestion.formula) }} />
                  </div>
                )}

                {/* Question Image (image & hybrid types) — stored as questionImage */}
                {(currentQuestion.questionImage || currentQuestion.imageUrl) && (
                  <div className="mb-4">
                    <img
                      src={currentQuestion.questionImage || currentQuestion.imageUrl}
                      alt="Question"
                      className="max-w-full max-h-64 rounded-xl border border-gray-200 object-contain"
                    />
                  </div>
                )}

                {/* Options — EAMCET style */}
                <div className="space-y-2.5 mt-5">
                  {['A', 'B', 'C', 'D'].map(opt => {
                    const optText = currentQuestion.options?.[opt];
                    const optImg  = currentQuestion.optionImages?.[opt];
                    const isSelected = currentAnswer?.selectedAnswer === opt;

                    return (
                      <label
                        key={opt}
                        onClick={() => handleAnswerSelect(opt)}
                        className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border-2 cursor-pointer transition-all duration-150 select-none ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                        }`}
                      >
                        {/* Radio button */}
                        <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'border-blue-600' : 'border-gray-400'
                        }`}>
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-blue-600" />
                          )}
                        </div>

                        {/* Option letter badge */}
                        <span className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-sm font-bold transition-colors ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {opt}
                        </span>

                        {/* Option content */}
                        <span className="exam-option-text flex-1 text-sm sm:text-base text-gray-800">
                          {optText && (
                            <span
                              dangerouslySetInnerHTML={{ __html: renderInline(optText) }}
                            />
                          )}
                          {optImg && (
                            <img
                              src={optImg}
                              alt={`Option ${opt}`}
                              className={`max-h-20 max-w-full rounded-md border border-gray-200 object-contain ${optText ? 'mt-1.5' : ''}`}
                            />
                          )}
                          {!optText && !optImg && (
                            <span className="text-gray-400 italic text-sm">—</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p>Question not available</p>
              </div>
            )}
          </div>

          {/* Bottom controls */}
          <div className="bg-gray-50 border-t px-4 py-3 flex flex-wrap items-center gap-2 flex-shrink-0">
            <button onClick={goToPrev} disabled={currentIndex === 0}
              className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 text-sm font-medium disabled:opacity-40">
              <ChevronLeft size={16} /> Prev
            </button>
            <button onClick={handleMarkForReview}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentAnswer?.isMarkedForReview
                  ? 'bg-amber-500 text-white'
                  : 'border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}>
              <Flag size={15} /> {currentAnswer?.isMarkedForReview ? 'Marked' : 'Mark for Review'}
            </button>
            <button onClick={handleClearResponse} disabled={!currentAnswer?.selectedAnswer}
              className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 text-sm font-medium disabled:opacity-40">
              <RotateCcw size={14} /> Clear
            </button>
            <div className="flex-1"></div>
            <button onClick={() => { setCurrentIndex(prev => prev + 1); }}
              disabled={currentIndex >= answers.length - 1}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-sm font-semibold disabled:opacity-40">
              Save & Next <ChevronRight size={16} />
            </button>
          </div>
        </main>

        {/* RIGHT PANEL - Question Palette */}
        <aside className="hidden lg:flex w-72 bg-white border-l border-gray-200 flex-col flex-shrink-0">
          {/* Student info */}
          <div className="bg-gray-50 border-b px-4 py-3">
            <p className="text-xs text-gray-500">Candidate</p>
            <p className="font-bold text-gray-800 text-sm">{attempt && 'Mock Test'}</p>
          </div>

          {/* Legend */}
          <div className="px-4 py-3 border-b">
            <p className="text-xs font-semibold text-gray-600 mb-2">Legend</p>
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              {[
                { color: 'bg-green-500', label: `Answered (${totalAnswered})` },
                { color: 'bg-red-500', label: `Not Answered (${answers.filter(a => a.isVisited && !a.selectedAnswer).length})` },
                { color: 'bg-gray-300', label: `Not Visited (${totalNotVisited})` },
                { color: 'bg-amber-400', label: `Marked (${totalMarked})` },
                { color: 'bg-violet-500', label: `Marked+Ans (${answers.filter(a => a.isMarkedForReview && a.selectedAnswer).length})` },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className={`w-4 h-4 rounded ${color} flex-shrink-0`}></span>
                  <span className="text-gray-600 truncate">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Question Grid */}
          <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-thin">
            {SUBJECTS.map(subject => {
              const subjectQs = subjectGroups[subject];
              if (!subjectQs || subjectQs.length === 0) return null;
              return (
                <div key={subject} className="mb-4">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">{subject}</p>
                  <div className="grid grid-cols-6 gap-1.5">
                    {subjectQs.map(({ index }) => {
                      const ans = answers[index];
                      const status = getQuestionStatus(ans);
                      const isCurrent = currentIndex === index;
                      return (
                        <button key={index} onClick={() => goToQuestion(index)}
                          className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${STATUS_CLASSES[status]} ${isCurrent ? 'ring-2 ring-primary-600 ring-offset-1 scale-110' : 'hover:scale-105'}`}>
                          {ans?.questionNumber || index + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="border-t p-4">
            <div className="text-xs text-gray-500 space-y-1 mb-3">
              <div className="flex justify-between"><span>Answered:</span><span className="font-bold text-green-600">{totalAnswered}</span></div>
              <div className="flex justify-between"><span>Not answered:</span><span className="font-bold text-red-500">{answers.filter(a => a.isVisited && !a.selectedAnswer).length}</span></div>
              <div className="flex justify-between"><span>Not visited:</span><span className="font-bold text-gray-500">{totalNotVisited}</span></div>
              <div className="flex justify-between"><span>Marked for review:</span><span className="font-bold text-amber-500">{totalMarked}</span></div>
            </div>
            <button onClick={() => setShowSubmitModal(true)} disabled={submitting}
              className="w-full btn-danger flex items-center justify-center gap-2 text-sm">
              <Send size={15} /> Submit Exam
            </button>
          </div>
        </aside>
      </div>

      {/* Mobile palette button */}
      <button className="lg:hidden fixed bottom-20 right-4 z-20 bg-primary-600 text-white rounded-full shadow-lg w-12 h-12 flex items-center justify-center text-xs font-bold"
        onClick={() => setPaletteOpen(!paletteOpen)}>
        {currentIndex + 1}
      </button>

      {/* Mobile palette drawer */}
      {paletteOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setPaletteOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">Question Palette</h3>
                <button onClick={() => setPaletteOpen(false)} className="text-gray-400">✕</button>
              </div>
              {SUBJECTS.map(subject => {
                const subjectQs = subjectGroups[subject];
                if (!subjectQs || subjectQs.length === 0) return null;
                return (
                  <div key={subject}>
                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">{subject}</p>
                    <div className="grid grid-cols-6 gap-1.5">
                      {subjectQs.map(({ index }) => {
                        const ans = answers[index];
                        const status = getQuestionStatus(ans);
                        const isCurrent = currentIndex === index;
                        return (
                          <button key={index} onClick={() => goToQuestion(index)}
                            className={`w-9 h-9 rounded-lg text-xs font-bold ${STATUS_CLASSES[status]} ${isCurrent ? 'ring-2 ring-primary-600' : ''}`}>
                            {ans?.questionNumber || index + 1}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 text-lg">Submit Exam?</h2>
                  <p className="text-gray-500 text-sm">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-6 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Answered:</span>
                  <span className="font-bold text-green-600">{totalAnswered} / {answers.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Not answered:</span>
                  <span className="font-bold text-red-500">{answers.length - totalAnswered}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Marked for review:</span>
                  <span className="font-bold text-amber-500">{totalMarked}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Time remaining:</span>
                  <span className="font-bold text-primary-700">{formatTime(timeLeft)}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowSubmitModal(false)} className="btn-secondary flex-1">
                  Continue Exam
                </button>
                <button onClick={() => { setShowSubmitModal(false); submitExam(false); }}
                  disabled={submitting}
                  className="btn-danger flex-1 flex items-center justify-center gap-2">
                  {submitting ? <div className="loader w-4 h-4 border-2"></div> : <Send size={16} />}
                  {submitting ? 'Submitting...' : 'Confirm Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back Navigation Warning Modal */}
      {showBackWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-fade-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-amber-600" size={24} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 text-lg">Back Navigation Blocked</h2>
                  <p className="text-gray-500 text-sm">You cannot go back during the exam</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-sm text-amber-800">
                <p className="font-semibold mb-1">⚠️ Exam in Progress</p>
                <p>Back navigation is disabled to maintain exam integrity. Your answers are continuously auto-saved.</p>
                <p className="mt-2">To exit, please <strong>Submit</strong> the exam using the Submit button.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBackWarning(false)}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-colors">
                  Continue Exam
                </button>
                <button
                  onClick={() => { setShowBackWarning(false); setShowSubmitModal(true); }}
                  className="flex-1 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 font-semibold py-2.5 px-4 rounded-xl transition-colors">
                  Submit Exam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
